import { useEffect, useMemo, useRef } from 'react';
import type { Frame } from '../store/types';
import { useCanvasStore } from '../store/canvas-store';
import { framesById, getRenderHtml } from '../store/commands';
import { ChatFrameView } from '../chrome/chat/ChatFrameView';
import { CodeFrameView } from '../chrome/code/CodeFrameView';
import { FxFrameView } from '../chrome/fx/FxFrameView';
import { NoteFrameView } from '../chrome/note/NoteFrameView';
import { StyleguideFrameView } from '../chrome/styleguide/StyleguideFrameView';
import { TsxRenderer } from './TsxRenderer';
import { useAssetResolvedHtml } from '../assets/useAssetResolvedHtml';
import { wireInlineSlotEdit } from '../chrome/inlineSlotEdit';
import { applyTheme } from './applyTheme';
import { useFrameScript } from '../script/useFrameScript';
import { useFrameMotion } from '../motion/useFrameMotion';
import { useFrameLottie } from '../lottie/useFrameLottie';
import { invalidateCanvas } from './canvasInvalidate';

/**
 * The inner content of a frame, routed by kind — the single per-kind renderer
 * shared by the canvas surface (`UnifiedFrameRoot`) and the isolated single-frame
 * preview tab (`IsolatedFrameApp`). Tool frames render their React view; design
 * frames (html free/main/instance) render live themed HTML via `DesignContent`;
 * tsx frames mount the React tree.
 *
 * `onInvalidate` is the canvas-repaint hook for animated scripts/motion. On the
 * unified canvas pass `invalidateCanvas` so script/motion writes trigger a
 * `canvas.requestPaint()`; in the DOM fallback / isolated preview it is a no-op.
 */
export function FrameContent({
  frame,
  byId,
  onInvalidate = invalidateCanvas,
}: {
  frame: Frame;
  byId: Map<string, Frame>;
  onInvalidate?: () => void;
}) {
  if (frame.kind === 'chat') return <ChatFrameView frame={frame} />;
  if (frame.kind === 'code') return <CodeFrameView frame={frame} />;
  if (frame.kind === 'fx') return <FxFrameView frame={frame} />;
  if (frame.kind === 'note') return <NoteFrameView frame={frame} />;
  if (frame.kind === 'styleguide') return <StyleguideFrameView frame={frame} />;
  if (frame.kind === 'tsxFree' || frame.kind === 'tsxMain' || frame.kind === 'tsxInstance') {
    return <TsxRenderer frame={frame} byId={byId} />;
  }
  return <DesignContent frame={frame} byId={byId} onInvalidate={onInvalidate} />;
}

/** Live HTML design-frame content: asset-resolved html + inline slot edit +
 *  theme cascade + scoped script + motion player. Shared by both render entries. */
export function DesignContent({
  frame,
  byId,
  onInvalidate = invalidateCanvas,
}: {
  frame: Frame;
  byId: Map<string, Frame>;
  onInvalidate?: () => void;
}) {
  const frames = useCanvasStore((s) => s.doc.frames);
  const activeThemeId = useCanvasStore((s) => s.doc.activeThemeId);
  const ref = useRef<HTMLDivElement>(null);
  const html = useMemo(() => getRenderHtml(frame, byId), [frame, byId]);
  const resolvedHtml = useAssetResolvedHtml(html);

  useEffect(() => {
    if (!ref.current) return;
    return wireInlineSlotEdit(ref.current, frame);
  }, [frame, resolvedHtml]);

  // Theme cascades to descendants via data-theme. Per design frame (own theme
  // else canvas active theme) so tool frames don't inherit theme color tokens.
  useEffect(() => {
    if (!ref.current) return;
    applyTheme(ref.current, frame, framesById(frames), activeThemeId);
  }, [frame, frames, resolvedHtml, activeThemeId]);

  // Scoped script + motion. ref points at the source-HTML root; writing inline
  // styles there and calling onInvalidate triggers a canvas repaint when needed.
  useFrameScript(ref, frame, onInvalidate);
  useFrameMotion(ref, frame);
  // Hydrate [data-lottie] elements (no-op without any). Pass resolvedHtml so the
  // hook re-mounts when async asset resolution rewrites innerHTML (which wipes
  // the SVG) — and so it scans the resolved (blob-ref'd) DOM.
  useFrameLottie(ref, frame, resolvedHtml);

  return (
    <div
      ref={ref}
      className="codeframe-content"
      // contain: layout — frame is its own containing block + stacking context
      // so frame-authored position:fixed / high z-index can't escape to cover
      // the app chrome.
      style={{ width: '100%', height: '100%', overflow: 'auto', contain: 'layout' }}
      dangerouslySetInnerHTML={{ __html: resolvedHtml }}
    />
  );
}
